import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../../../lib/supabase'
import { createReportOnceRetry } from '../../../../lib/reportError'

export const PENDING_DEVICE_POLL_INTERVAL_MS = 5000

export interface Device {
  id: string
  name: string
  branchId: string
  branchName: string
  status: 'active' | 'inactive' | 'pending'
  lastActiveAt: Date | null
  lastActivityAt: Date | null
  pairingCode: string | null
  pairingCodeExpiresAt: Date | null
}

async function fetchDevices(): Promise<Device[]> {
  const [{ data: devicesData, error: devicesError }, { data: activityData, error: activityError }] =
    await Promise.all([
      supabase
        .from('devices')
        .select('id, name, status, branch_id, last_active_at, pairing_code, pairing_code_expires_at, branch:branches(name)')
        .order('created_at', { ascending: false }),
      supabase.rpc('get_device_last_activity'),
    ])

  if (devicesError) throw devicesError
  if (activityError) throw activityError

  const activityMap = new Map<string, Date>()
  for (const row of activityData ?? []) {
    if (row.device_id && row.last_activity_at) {
      activityMap.set(row.device_id, new Date(row.last_activity_at))
    }
  }

  return (devicesData ?? []).map((d) => ({
    id: d.id,
    name: d.name,
    branchId: d.branch_id as string,
    branchName: (d.branch as unknown as { name: string } | null)?.name ?? '',
    status: d.status as 'active' | 'inactive' | 'pending',
    lastActiveAt: d.last_active_at ? new Date(d.last_active_at) : null,
    lastActivityAt: activityMap.get(d.id) ?? null,
    pairingCode: d.pairing_code as string | null,
    pairingCodeExpiresAt: d.pairing_code_expires_at ? new Date(d.pairing_code_expires_at) : null,
  }))
}

interface UseDevicesOptions {
  pollPendingDevices?: boolean
  pollPendingDeviceId?: string | null
  enabled?: boolean
}

export function useDevices(options: UseDevicesOptions = {}) {
  const hasReportedRef = useRef(false)

  return useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: options.enabled,
    refetchInterval: (query) => {
      const devices = query.state.data
      if (options.pollPendingDeviceId) {
        const selectedDevice = devices?.find((device) => device.id === options.pollPendingDeviceId)
        return selectedDevice?.status === 'pending' ? PENDING_DEVICE_POLL_INTERVAL_MS : false
      }
      if (options.pollPendingDevices) {
        return devices?.some((device) => device.status === 'pending') ? PENDING_DEVICE_POLL_INTERVAL_MS : false
      }
      return false
    },
    // Polls every 5s while a device is pending — retries normally so a
    // transient blip self-heals, but reports at most once for as long as
    // this hook stays mounted (see createReportOnceRetry).
    retry: createReportOnceRetry(hasReportedRef),
  })
}
